<div class="profile-container" id="profile-container">
  <div class="profile-header" id="profile-header">
    <h1 class="profile-title" id="profile-title">{{ translate('profile.title') }}</h1>
  </div>

  <div class="profile-content" id="profile-content">
    <div class="alert alert-success" *ngIf="successMessage" id="profile-success-message">
      <i class="fas fa-check-circle"></i> {{ successMessage }}
    </div>

    <div class="alert alert-danger" *ngIf="errorMessage" id="profile-error-message">
      <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
    </div>

    <div *ngIf="isLoading" class="profile-loader" id="profile-loader">
      <div class="spinner-border text-primary" role="status" id="profile-loader-spinner">
        <span class="visually-hidden" id="profile-loader-label">{{ translate('profile.loading') }}</span>
      </div>
      <p id="profile-loader-data">{{ translate('profile.loading_data') }}</p>
    </div>

    <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" class="profile-form" *ngIf="!isLoading" id="profile-form">
      <div class="profile-card glass-effect" id="profile-section">
        <div class="profile-hero" id="profile-avatar">
          <div class="hero-avatar clickable" id="profile-avatar-image" (click)="onAvatarClick()"
            [class.has-photo]="hasPhoto"
            [style.backgroundImage]="hasPhoto ? ('url(' + userProfile?.urlPhoto + ')') : null"
            [attr.aria-label]="translate('profile.change_photo')" role="button" tabindex="0">
            <span class="hero-avatar-initials" *ngIf="!hasPhoto" aria-hidden="true">{{ initials }}</span>
            <div class="avatar-overlay" *ngIf="!uploadingPhoto">
              <i class="fas fa-camera"></i>
              <span>{{ translate('profile.change_photo') }}</span>
            </div>
            <div class="avatar-loader" *ngIf="uploadingPhoto">
              <div class="spinner-border text-primary" role="status"></div>
            </div>
          </div>
          <div class="hero-info" id="profile-avatar-info">
            <h2 class="hero-name" id="profile-avatar-name">{{ userProfile?.name }}</h2>
            <div class="hero-meta">
              <span class="hero-badge" id="profile-avatar-role">
                <i class="fas fa-user-shield"></i> {{ getRoleLabel(userProfile?.role) }}
              </span>
              <span class="hero-company" *ngIf="userProfile?.company" id="profile-avatar-company">
                <i class="fas fa-building"></i> {{ userProfile?.company }}
              </span>
            </div>
          </div>
        </div>

        <section class="form-section" id="profile-section-personal">
          <h3 class="section-title">{{ translate('profile.section_personal') }}</h3>
          <div class="form-grid">
            <div class="form-group span-2" id="profile-form-group-name">
              <label for="name" id="profile-label-name">{{ translate('profile.name') }}</label>
              <input type="text" id="name" class="form-control" formControlName="name">
              <div *ngIf="profileForm.get('name')?.invalid && profileForm.get('name')?.touched" class="form-error" id="profile-error-name">
                {{ translate('profile.name_required') }}
              </div>
            </div>

            <div class="form-group locked" id="profile-form-group-email">
              <label for="email" id="profile-label-email">{{ translate('profile.email') }}</label>
              <div class="locked-field">
                <input type="email" id="email" class="form-control" formControlName="email">
                <i class="fas fa-lock lock-icon" aria-hidden="true"></i>
              </div>
              <div class="form-text" id="profile-form-text-email">
                <i class="fas fa-lock"></i> {{ translate('profile.email_managed') }}
              </div>
            </div>

            <div class="form-group" id="profile-form-group-jobTitle">
              <label for="jobTitle" id="profile-label-jobTitle">{{ translate('profile.job_title') }}</label>
              <input type="text" id="jobTitle" class="form-control" formControlName="jobTitle">
            </div>
          </div>
        </section>

        <section class="form-section" id="profile-section-org">
          <h3 class="section-title">{{ translate('profile.section_organization') }}</h3>
          <div class="form-grid">
            <div class="form-group" id="profile-form-group-company">
              <label for="company" id="profile-label-company">{{ translate('profile.company') }}</label>
              <input type="text" id="company" class="form-control" formControlName="company">
            </div>

            <div class="form-group locked" id="profile-form-group-role">
              <label for="role" id="profile-label-role">{{ translate('profile.role') }}</label>
              <div class="locked-field">
                <input type="text" id="role" class="form-control" formControlName="role">
                <i class="fas fa-lock lock-icon" aria-hidden="true"></i>
              </div>
              <div class="form-text" id="profile-form-text-role">
                <i class="fas fa-lock"></i> {{ translate('profile.role_managed') }}
              </div>
            </div>

            <div class="form-group" id="profile-form-group-organizationType">
              <label for="organizationType" id="profile-label-organizationType">{{ translate('profile.organization_type') }}</label>
              <select id="organizationType" class="form-control" formControlName="organizationType">
                <option *ngFor="let type of organizationTypes" [value]="type.value" [id]="'profile-orgtype-option-' + type.value">
                  {{ translate(type.label) }}
                </option>
              </select>
            </div>

            <div class="form-group" id="profile-form-group-organizationSize">
              <label for="organizationSize" id="profile-label-organizationSize">{{ translate('profile.organization_size') }}</label>
              <select id="organizationSize" class="form-control" formControlName="organizationSize">
                <option *ngFor="let size of organizationSizes" [value]="size.value" [id]="'profile-orgsize-option-' + size.value">
                  {{ translate(size.label) }}
                </option>
              </select>
            </div>

            <div class="form-group" id="profile-form-group-country">
              <label for="country" id="profile-label-country">{{ translate('profile.country') }}</label>
              <select id="country" class="form-control" formControlName="country">
                <option value="">{{ translate('profile.country_select') }}</option>
                <option *ngFor="let c of countries" [value]="c" [id]="'profile-country-option-' + c">{{ c }}</option>
              </select>
            </div>
          </div>
        </section>

        <section class="form-section" id="profile-section-prefs">
          <h3 class="section-title">{{ translate('profile.section_preferences') }}</h3>
          <div class="form-grid">
            <div class="form-group span-2" id="profile-form-group-language">
              <label id="profile-label-language">{{ translate('wizard.language_preference') }}</label>
              <div class="language-selector" id="profile-language-selector">
                <button type="button" class="lang-chip" [class.is-active]="profileForm.get('language')?.value === 'es'"
                  (click)="profileForm.patchValue({language: 'es'}); languageService.setLanguage('es');">
                  🇪🇸 {{ translate('settings.spanish') }}
                </button>
                <button type="button" class="lang-chip" [class.is-active]="profileForm.get('language')?.value === 'en'"
                  (click)="profileForm.patchValue({language: 'en'}); languageService.setLanguage('en');">
                  🇬🇧 {{ translate('settings.english') }}
                </button>
                <button type="button" class="lang-chip" [class.is-active]="profileForm.get('language')?.value === 'pt'"
                  (click)="profileForm.patchValue({language: 'pt'}); languageService.setLanguage('pt');">
                  🇵🇹 {{ translate('settings.portuguese') }}
                </button>
                <button type="button" class="lang-chip" [class.is-active]="profileForm.get('language')?.value === 'fr'"
                  (click)="profileForm.patchValue({language: 'fr'}); languageService.setLanguage('fr');">
                  🇫🇷 {{ translate('settings.french') }}
                </button>
                <button type="button" class="lang-chip" [class.is-active]="profileForm.get('language')?.value === 'ja'"
                  (click)="profileForm.patchValue({language: 'ja'}); languageService.setLanguage('ja');">
                  🇯🇵 {{ translate('settings.japanese') }}
                </button>
              </div>
            </div>

            <div class="form-group span-2" id="profile-form-group-timezone">
              <label for="timezone" id="profile-label-timezone">{{ translate('wizard.timezone') }}</label>
              <input #tzSearch type="text" class="form-control mb-2" [attr.placeholder]="translate('wizard.timezone_search')"
                (input)="onTimezoneQueryChange(tzSearch.value)" (keyup.enter)="onTimezoneEnter()" [value]="timezoneQuery" id="profile-timezone-search" />
              <select id="timezone" class="form-control" formControlName="timezone">
                <option value="" disabled>{{ translate('wizard.timezone_select') }}</option>
                <option *ngFor="let tz of timezones" [value]="tz.value" [id]="'profile-timezone-option-' + tz.value">{{ tz.label }}</option>
              </select>
              <div *ngIf="timezones.length === 0" class="form-text" id="profile-timezone-no-results">{{ translate('wizard.timezone_no_results') }}</div>
            </div>
          </div>
        </section>
      </div>

      <div class="profile-actions" id="profile-actions">
        <button type="submit" class="btn btn-primary" [disabled]="profileForm.invalid || isLoading" id="profile-save-btn">
          <i class="fas fa-save" id="profile-save-icon"></i> {{ translate('profile.save') }}
        </button>
      </div>
    </form>
  </div>
</div>
